/**
 * Smart Workspace Registry (Feb 2026 v8)
 * ──────────────────────────────────────
 * Nexus detects the project type from `project_stack` (set by the
 * scaffolder on the workspace document) and shows a CONTEXTUAL set of
 * quick-actions in the chat UI.
 *
 * Adding a new workspace type is a two-step change:
 *   1. Extend `_STACK_PATTERNS` in `backend/engine/scaffolder.py` so
 *      that new user prompts route to your stack.
 *   2. Add a new entry below with a `label`, `icon`, `quickPrompts` and
 *      optional `sections`.
 *
 * The whole design intentionally avoids importing React components —
 * this is a plain data registry so both the chat panel and any future
 * side-drawer can consume it independently.
 */

export interface QuickPrompt {
  /** Short human label shown on the chip. */
  label: string;
  /** The actual text that gets inserted into the chat input on click. */
  prompt: string;
  /** Lucide icon name (dynamically imported by consumers). */
  icon?: string;
  /** Optional group so we can render section headers in a drawer. */
  group?: string;
}

export interface WorkspaceKind {
  /** Machine key — matches `Workspace.project_stack` on the backend. */
  key: string;
  /** Human name shown in badges + drawer titles. */
  label: string;
  /** One-line description shown as a subtitle in the header/drawer. */
  tagline: string;
  /** Accent color for badges. HSL string that lands in inline styles. */
  accent: string;
  /** Curated quick-actions the user can tap to insert a prompt. */
  quickPrompts: QuickPrompt[];
}


const LANDING_PROMPTS: QuickPrompt[] = [
  { group: 'Hero', label: 'Cambiar hero', prompt: 'Reescribe el hero con un titular más impactante y una llamada a la acción secundaria.' },
  { group: 'Hero', label: 'Ajustar CTA', prompt: 'Cambia el botón principal a "Reservar ahora" y hazlo más visible.' },
  { group: 'Secciones', label: 'Añadir FAQ', prompt: 'Agrega una sección de preguntas frecuentes con 5 preguntas típicas antes del footer.' },
  { group: 'Secciones', label: 'Añadir testimonios', prompt: 'Añade una sección de testimonios de clientes con 3 reseñas cortas.' },
  { group: 'Secciones', label: 'Añadir galería', prompt: 'Añade una galería con 6 imágenes representativas (placeholders si es necesario).' },
  { group: 'Estilo', label: 'Paleta azul', prompt: 'Cambia la paleta principal a tonos azules (#3b82f6 y variantes) manteniendo la legibilidad.' },
  { group: 'Estilo', label: 'Modo oscuro', prompt: 'Convierte todo el sitio a modo oscuro con contraste alto.' },
  { group: 'Estilo', label: 'Tipografía moderna', prompt: 'Cambia la tipografía a Inter para el cuerpo y Space Grotesk para los titulares.' },
  { group: 'Contacto', label: 'Formulario contacto', prompt: 'Añade un formulario de contacto con campos nombre, email, teléfono y mensaje.' },
  { group: 'Contacto', label: 'Mapa integrado', prompt: 'Añade un mapa embebido de Google Maps con la ubicación del negocio.' },
  { group: 'SEO', label: 'Optimizar SEO', prompt: 'Añade meta title, meta description y Open Graph tags optimizados para SEO.' },
  { group: 'Responsive', label: 'Mejorar móvil', prompt: 'Revisa el diseño responsive y arregla cualquier problema visible en móvil.' },
];


const APP_PROMPTS: QuickPrompt[] = [
  { group: 'UI', label: 'Rediseñar UI', prompt: 'Rediseña la interfaz principal con un layout más limpio y jerarquía visual clara.' },
  { group: 'UI', label: 'Añadir toolbar', prompt: 'Añade una barra de herramientas superior con las acciones principales visibles.' },
  { group: 'Componentes', label: 'Nuevo formulario', prompt: 'Añade un formulario con validación en cliente para capturar datos del usuario.' },
  { group: 'Componentes', label: 'Tabla de datos', prompt: 'Añade una tabla ordenable con búsqueda para los elementos existentes.' },
  { group: 'Componentes', label: 'Modal de edición', prompt: 'Añade un modal para editar cada elemento con foco automático y cierre por Escape.' },
  { group: 'Estado', label: 'Persistir datos', prompt: 'Guarda el estado en localStorage para que sobreviva a recargas de página.' },
  { group: 'Estado', label: 'Deshacer/Rehacer', prompt: 'Implementa acciones de deshacer/rehacer con historial de al menos 20 pasos.' },
  { group: 'Flujos', label: 'Onboarding', prompt: 'Añade un flujo de onboarding de 3 pasos que muestre las funcionalidades principales al abrir.' },
  { group: 'Flujos', label: 'Estados vacíos', prompt: 'Diseña estados vacíos amables para cuando no hay datos aún.' },
  { group: 'Datos', label: 'Exportar JSON', prompt: 'Añade un botón para exportar los datos actuales como JSON descargable.' },
  { group: 'Datos', label: 'Importar CSV', prompt: 'Añade la posibilidad de importar datos desde un archivo CSV.' },
  { group: 'Datos', label: 'Datos de ejemplo', prompt: 'Carga 5 elementos de ejemplo al inicio para que el usuario vea la app llena.' },
];


const CLI_PROMPTS: QuickPrompt[] = [
  { group: 'Comandos', label: 'Nuevo comando', prompt: 'Añade un subcomando nuevo para procesar un tipo adicional de archivo.' },
  { group: 'Comandos', label: 'Flags avanzados', prompt: 'Añade flags --verbose, --dry-run y --output para el comando principal.' },
  { group: 'UX', label: 'Barra de progreso', prompt: 'Muestra una barra de progreso mientras el comando procesa datos.' },
  { group: 'UX', label: 'Salida en colores', prompt: 'Usa colores (rich/click.style) para diferenciar errores, warnings e info.' },
  { group: 'Testing', label: 'Añadir tests', prompt: 'Añade tests con pytest para los flujos principales del CLI.' },
];


export const WORKSPACE_KINDS: Record<string, WorkspaceKind> = {
  landing_page: {
    key: 'landing_page',
    label: 'Landing',
    tagline: 'Editor especializado en marketing y conversión',
    accent: 'hsl(200 90% 60%)',
    quickPrompts: LANDING_PROMPTS,
  },
  static_site: {
    key: 'static_site',
    label: 'Sitio estático',
    tagline: 'Editor para páginas estáticas y sitios de contenido',
    accent: 'hsl(200 90% 60%)',
    quickPrompts: LANDING_PROMPTS,
  },
  fastapi_react_mongo: {
    key: 'fastapi_react_mongo',
    label: 'Aplicación',
    tagline: 'Editor para desarrollo de aplicaciones interactivas',
    accent: 'hsl(155 65% 55%)',
    quickPrompts: APP_PROMPTS,
  },
  node_express: {
    key: 'node_express',
    label: 'Aplicación',
    tagline: 'Editor para aplicaciones Node.js + frontend',
    accent: 'hsl(155 65% 55%)',
    quickPrompts: APP_PROMPTS,
  },
  python_cli: {
    key: 'python_cli',
    label: 'CLI',
    tagline: 'Editor para herramientas de línea de comandos',
    accent: 'hsl(280 70% 65%)',
    quickPrompts: CLI_PROMPTS,
  },
};


/** Fallback returned when we don't recognise the stack yet. */
export const DEFAULT_WORKSPACE_KIND: WorkspaceKind = {
  key: 'unknown',
  label: 'Workspace',
  tagline: 'Nexus está detectando el tipo de proyecto…',
  accent: 'hsl(220 15% 60%)',
  quickPrompts: [],
};


export function resolveWorkspaceKind(
  stack: string | null | undefined,
): WorkspaceKind {
  if (!stack) return DEFAULT_WORKSPACE_KIND;
  return WORKSPACE_KINDS[stack] || DEFAULT_WORKSPACE_KIND;
}
